import React from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Button,
  Grid,
  Chip,
  LinearProgress,
  Link,
} from '@material-ui/core';
import { compose } from 'recompose';
import { withStyles } from '@material-ui/styles';
import { withRouter } from 'react-router-dom';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';
import { Auth, Hub } from 'aws-amplify';
import { connect } from 'react-redux';
import { openSnackbar } from 'Components/content/notifier';

import {
  setUserSignedIn,
  setPostSigninRedirectPath,
  getLoggedInUserDetails,
} from 'Actions/authUser';
import { APP_ROUTES } from 'Utils/appShell';
import { AUTH_STATES } from 'Utils/constants';

const styles = theme => ({
  errorChip: {
    borderRadius: 4,
  },
  cursorPointer: {
    cursor: 'pointer',
  },
});

class SignInForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      uname: '',
      upass: '',
      signInError: null,
      forceSetNewPassword: false,
      cognitoUser: null,
      newPassword: '',
      repeatPassword: '',
      actionInProgress: false,
    };
    this.handleValueChange = this.handleValueChange.bind(this);
    this.handleSignIn = this.handleSignIn.bind(this);
    this.handleNewPassword = this.handleNewPassword.bind(this);
    this.redirectSignedInUser = this.redirectSignedInUser.bind(this);
  }

  componentDidMount() {
    //custom rule for password matching in (re)set password form
    ValidatorForm.addValidationRule('isPasswordMatch', value => {
      if (value !== this.state.newPassword) {
        return false;
      }
      return true;
    });
  }

  componentWillUnmount() {
    // remove custom rule from validator
    ValidatorForm.removeValidationRule('isPasswordMatch');
  }

  handleValueChange = fieldName => event => {
    this.setState({
      [fieldName]: event.target.value,
    });
  };

  redirectSignedInUser = () => {
    const { setUserSignedIn, history } = this.props;
    setUserSignedIn();
    history.push(APP_ROUTES.AUTH_SIGNIN_LANDING);
  };

  handleSignIn = async event => {
    const { uname, upass } = this.state;
    this.setState({ signInError: null, actionInProgress: true });
    try {
      const user = await Auth.signIn(uname, upass);
      if (
        user.challengeName &&
        user.challengeName === 'NEW_PASSWORD_REQUIRED'
      ) {
        // user need to set a password to continue
        this.setState({
          forceSetNewPassword: true,
          signInError: 'Please set a password to continue',
          cognitoUser: user,
          actionInProgress: false,
        });
      } else {
        // user signed in successfully
        this.redirectSignedInUser();
      }
    } catch (err) {
      console.error('Error during signin: ', err);
      let errorToDisplay = 'Unknown error during sign in, try again';
      if (err.code === 'UserNotConfirmedException') {
        //
      } else if (err.code === 'PasswordResetRequiredException') {
        //
      } else if (
        ['NotAuthorizedException', 'UserNotFoundException'].includes(err.code)
      ) {
        errorToDisplay = 'Incorrect Email ID or password';
      } else {
        //FIXME: handle using error boundaries
        console.error('Unknown error during sign in: ', err);
      }

      this.setState({ signInError: errorToDisplay, actionInProgress: false });
    }
  };

  handleNewPassword = async event => {
    const { cognitoUser, newPassword } = this.state;
    this.setState({ signInError: null, actionInProgress: true });
    try {
      await Auth.completeNewPassword(cognitoUser, newPassword);
      this.redirectSignedInUser();
    } catch (err) {
      console.error('Error setting new password: ', err);
      this.setState({
        signInError: 'Something went wrong, please try again',
        actionInProgress: false,
      });
    }
  };

  render() {
    const {
      uname,
      upass,
      newPassword,
      repeatPassword,
      signInError,
      forceSetNewPassword,
      actionInProgress,
    } = this.state;
    const { classes, history } = this.props;
    return forceSetNewPassword ? (
      <ValidatorForm onSubmit={this.handleNewPassword}>
        <Grid container direction="column" spacing={2} justify="space-evenly">
          <Grid item>
            <TextValidator
              label="New Password"
              name="new-password"
              type="password"
              value={newPassword}
              onChange={this.handleValueChange('newPassword')}
              validators={['required']}
              errorMessages={['password is required']}
              autoFocus
              disabled={actionInProgress}
            />
          </Grid>
          <Grid item>
            <TextValidator
              label="Confirm Password"
              name="repeat-password"
              type="password"
              value={repeatPassword}
              onChange={this.handleValueChange('repeatPassword')}
              validators={['required', 'isPasswordMatch']}
              errorMessages={[
                'password is required',
                'passwords did not match',
              ]}
              disabled={actionInProgress}
            />
          </Grid>
          <Grid item>
            <Typography variant="caption" color="primary">
              Password must contain atleast one Capital letter, one numeric, one
              special characters. For eg. Abcdef@123
            </Typography>
          </Grid>
          {signInError && (
            <Grid item>
              <Chip
                label={signInError}
                variant="outlined"
                color="primary"
                className={classes.errorChip}
              />
            </Grid>
          )}
          <Grid item>
            {actionInProgress ? (
              <>
                <Typography variant="caption" color="primary">
                  Setting new password
                </Typography>
                <LinearProgress variant="indeterminate" color="primary" />
              </>
            ) : (
              <Button
                variant="contained"
                color="primary"
                className={'fitContentWidth'}
                type="submit"
                disabled={actionInProgress}
              >
                Submit
              </Button>
            )}
          </Grid>
        </Grid>
      </ValidatorForm>
    ) : (
      <ValidatorForm onSubmit={this.handleSignIn}>
        <Grid container direction="column" spacing={2} justify="space-evenly">
          <Grid item>
            <TextValidator
              label="Email ID"
              name="email"
              value={uname}
              onChange={this.handleValueChange('uname')}
              validators={['required', 'isEmail']}
              errorMessages={['Email ID is required', 'Email ID is not valid']}
              autoFocus
              disabled={actionInProgress}
            />
          </Grid>
          <Grid item>
            <TextValidator
              label="Password"
              name="password"
              type="password"
              value={upass}
              onChange={this.handleValueChange('upass')}
              validators={['required']}
              errorMessages={['password is required']}
              disabled={actionInProgress}
            />
          </Grid>
          {signInError && (
            <Grid item>
              <Chip
                label={signInError}
                variant="outlined"
                color="primary"
                className={classes.errorChip}
              />
            </Grid>
          )}
          <Grid item>
            {actionInProgress ? (
              <>
                <Typography variant="caption" color="primary">
                  Signing you in
                </Typography>
                <LinearProgress variant="indeterminate" color="primary" />
              </>
            ) : (
              <Button
                variant="contained"
                color="primary"
                className={'fitContentWidth'}
                type="submit"
                disabled={actionInProgress}
              >
                Login
              </Button>
            )}
          </Grid>
          <Grid item>
            <Link
              component="button"
              variant="body2"
              className={classes.cursorPointer}
              onClick={() => history.push(APP_ROUTES.AUTH_FORGOT_PASSWORD)}
            >
              Forgot Password
            </Link>
          </Grid>
        </Grid>
      </ValidatorForm>
    );
  }
}

SignInForm.propTypes = {
  classes: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  setUserSignedIn: PropTypes.func.isRequired,
  setPostSigninRedirectPath: PropTypes.func.isRequired,
  redirectPathPostSignIn: PropTypes.string.isRequired,
};

const mapStateToProps = state => ({
  redirectPathPostSignIn: state.authUser.redirectPathPostSignIn,
  userAuthState: state.authUser.userAuthState,
});

SignInForm = compose(
  withStyles(styles, { withTheme: true }),
  withRouter,
  connect(
    mapStateToProps,
    {
      setUserSignedIn,
      setPostSigninRedirectPath,
    }
  )
)(SignInForm);

class SignInLanding extends React.Component {
  constructor(props) {
    super(props);
    this.state = { authFailed: false };
    this.handlePostSignin = this.handlePostSignin.bind(this);
  }

  componentDidMount() {
    const { userAuthState } = this.props;

    if (userAuthState === AUTH_STATES.SIGNING_IN) {
      // handle redirection from hosted UI or IdP auth flows
      Hub.listen('auth', ({ payload: { event, data } }) => {
        switch (event) {
          case 'signIn':
            console.debug('received data from auth flow: ', data);
            // FIXME: check and move logic to customOAuthState case below
            this.handlePostSignin();
            break;
          case 'customOAuthState':
            console.debug('received custom state from auth flow: ', data);
            // FIXME: check and move logic from signIn case above
            break;
          case 'signIn_failure':
            console.debug('received sign in failure from auth flow: ', data);
            // FIXME: handle auth failure
            this.setState({ authFailed: true });
        }
      });
    } else if (userAuthState === AUTH_STATES.SIGNED_IN) {
      // handle redirection from sign-in form (for username/pwd auth flow)
      this.handlePostSignin();
    }
  }

  componentWillUnmount() {
    Hub.remove('auth');
  }

  handlePostSignin(redirectPath) {
    const {
      redirectPathPostSignIn,
      setPostSigninRedirectPath,
      setUserSignedIn,
      getLoggedInUserDetails,
      history,
    } = this.props;
    // set state to user signed in
    setUserSignedIn();
    // load metadata
    getLoggedInUserDetails();
    // reset redirect path
    setPostSigninRedirectPath();
    // redirect to target path
    history.push(redirectPath || redirectPathPostSignIn);
  }

  render() {
    const { classes } = this.props;
    const { authFailed } = this.state;

    return authFailed ? (
      <Typography variant="subtitle1">
        You are now signed out.&nbsp;Click here to&nbsp;
        <Link
          Component="button"
          variant="body2"
          className={classes.cursorPointer}
          onClick={() => history.push(APP_ROUTES.AUTH_SIGNIN)}
        >
          Sign In
        </Link>
        &nbsp;again.
      </Typography>
    ) : (
      <>
        <Typography variant="subtitle1">
          You are now signed in. Redirecting to the portal in a moment....
        </Typography>
        <LinearProgress variant="query" color="secondary" />
      </>
    );
  }
}

SignInLanding.propTypes = {
  classes: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  setPostSigninRedirectPath: PropTypes.func.isRequired,
  setUserSignedIn: PropTypes.func.isRequired,
  getLoggedInUserDetails: PropTypes.func.isRequired,
  redirectPathPostSignIn: PropTypes.string.isRequired,
  userAuthState: PropTypes.string.isRequired,
};

SignInLanding = compose(
  withStyles(styles, { withTheme: true }),
  withRouter,
  connect(
    mapStateToProps,
    { setPostSigninRedirectPath, setUserSignedIn, getLoggedInUserDetails }
  )
)(SignInLanding);

class ForgotPassword extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      uname: '',
      forgotPasswordError: null,
      setNewPassword: false,
      verificationCode: '',
      cognitoUser: null,
      newPassword: '',
      repeatPassword: '',
      actionInProgress: false,
    };
    this.handleValueChange = this.handleValueChange.bind(this);
    this.handleForgotPassword = this.handleForgotPassword.bind(this);
    this.handleNewPassword = this.handleNewPassword.bind(this);
  }

  componentDidMount() {
    //custom rule for password matching in (re)set password form
    ValidatorForm.addValidationRule('isPasswordMatch', value => {
      if (value !== this.state.newPassword) {
        return false;
      }
      return true;
    });
  }

  componentWillUnmount() {
    // remove custom rule from validator
    ValidatorForm.removeValidationRule('isPasswordMatch');
  }

  handleValueChange = fieldName => event => {
    this.setState({
      [fieldName]: event.target.value,
    });
  };

  handleForgotPassword = async event => {
    const { uname } = this.state;
    this.setState({ forgotPasswordError: null, actionInProgress: true });
    try {
      const user = await Auth.forgotPassword(uname);
      this.setState({
        setNewPassword: true,
        forgotPasswordError: 'Sent a verification code to ' + uname,
        cognitoUser: user,
        actionInProgress: false,
      });
    } catch (err) {
      console.error('Error during forgot password flow: ', err);
      let errorToDisplay =
        'Unknown error during forgot password flow, try again';
      if (err.code === 'UserNotConfirmedException') {
        //
      } else if (err.code === 'PasswordResetRequiredException') {
        //
      } else if (
        ['NotAuthorizedException', 'UserNotFoundException'].includes(err.code)
      ) {
        errorToDisplay = 'Incorrect Email ID or password';
      } else {
        //FIXME: handle using error boundaries
        console.error('Unknown error during forgot password flow: ', err);
      }

      this.setState({
        forgotPasswordError: errorToDisplay,
        actionInProgress: false,
      });
    }
  };

  handleNewPassword = async event => {
    const { uname, newPassword, verificationCode } = this.state;
    const { history } = this.props;
    this.setState({ forgotPasswordError: null, actionInProgress: true });
    try {
      await Auth.forgotPasswordSubmit(uname, verificationCode, newPassword);
      openSnackbar({
        message:
          'Your password has been changed successfully. Please try logging in using your email and new password.',
        variant: 'success',
      });
      history.push(APP_ROUTES.AUTH_SIGNIN);
    } catch (err) {
      console.error('Error setting new password: ', err);
      let errorToDisplay =
        'Unknown error during forgot password flow, try again';
      if (err.code === 'CodeMismatchException') {
        errorToDisplay = err.message;
      } else if (err.code === 'InvalidParameterException') {
        errorToDisplay = 'Invalid Password';
      }
      this.setState({
        forgotPasswordError: errorToDisplay,
        actionInProgress: false,
      });
    }
  };

  render() {
    const {
      uname,
      newPassword,
      repeatPassword,
      forgotPasswordError,
      setNewPassword,
      actionInProgress,
      verificationCode,
    } = this.state;
    const { classes } = this.props;
    return setNewPassword ? (
      <ValidatorForm onSubmit={this.handleNewPassword}>
        <Grid container direction="column" spacing={2} justify="space-evenly">
          <Grid item>
            <TextValidator
              label="Verification Code"
              name="verification-code"
              type="password"
              value={verificationCode}
              onChange={this.handleValueChange('verificationCode')}
              validators={['required']}
              errorMessages={['Verification code is required']}
              autoFocus
              disabled={actionInProgress}
            />
          </Grid>
          <Grid item>
            <TextValidator
              label="New Password"
              name="new-password"
              type="password"
              value={newPassword}
              onChange={this.handleValueChange('newPassword')}
              validators={['required']}
              errorMessages={['password is required']}
              disabled={actionInProgress}
            />
          </Grid>
          <Grid item>
            <TextValidator
              label="Confirm Password"
              name="repeat-password"
              type="password"
              value={repeatPassword}
              onChange={this.handleValueChange('repeatPassword')}
              validators={['required', 'isPasswordMatch']}
              errorMessages={[
                'password is required',
                'passwords did not match',
              ]}
              disabled={actionInProgress}
            />
          </Grid>
          <Grid item>
            <Typography variant="caption" color="primary">
              Password must contain atleast one Capital letter, one numeric, one
              special characters. For eg. Abcdef@123
            </Typography>
          </Grid>
          {forgotPasswordError && (
            <Grid item>
              <Chip
                label={forgotPasswordError}
                variant="outlined"
                color="primary"
                className={classes.errorChip}
              />
            </Grid>
          )}
          <Grid item>
            {actionInProgress ? (
              <>
                <Typography variant="caption" color="primary">
                  Setting new password
                </Typography>
                <LinearProgress variant="indeterminate" color="primary" />
              </>
            ) : (
              <Button
                variant="contained"
                color="primary"
                className={'fitContentWidth'}
                type="submit"
                disabled={actionInProgress}
              >
                Submit
              </Button>
            )}
          </Grid>
        </Grid>
      </ValidatorForm>
    ) : (
      <ValidatorForm onSubmit={this.handleForgotPassword}>
        <Grid container direction="column" spacing={2} justify="space-evenly">
          <Grid item>
            <TextValidator
              label="Email ID"
              name="email"
              value={uname}
              onChange={this.handleValueChange('uname')}
              validators={['required', 'isEmail']}
              errorMessages={['Email ID is required', 'Email ID is not valid']}
              autoFocus
              disabled={actionInProgress}
            />
          </Grid>
          <Grid item>
            <span className="error">
              {'Provide your registered email with Flytta.'}
            </span>
          </Grid>
          {forgotPasswordError && (
            <Grid item>
              <Chip
                label={forgotPasswordError}
                variant="outlined"
                color="primary"
                className={classes.errorChip}
              />
            </Grid>
          )}
          <Grid item>
            {actionInProgress ? (
              <>
                <Typography variant="caption" color="primary">
                  Starting forgot password flow
                </Typography>
                <LinearProgress variant="indeterminate" color="primary" />
              </>
            ) : (
              <Button
                variant="contained"
                color="primary"
                className={'fitContentWidth'}
                type="submit"
                disabled={actionInProgress}
              >
                Submit
              </Button>
            )}
          </Grid>
        </Grid>
      </ValidatorForm>
    );
  }
}

ForgotPassword.propTypes = {
  classes: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

ForgotPassword = compose(
  withStyles(styles, { withTheme: true }),
  withRouter,
  connect(mapStateToProps)
)(ForgotPassword);

export { SignInForm, SignInLanding, ForgotPassword };
